import { zodResolver } from '@hookform/resolvers/zod';
import { useCallback, useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { toast } from 'sonner';
import { z } from 'zod';
import { useCurrentUserCustomer, useUpdateCustomer } from '@/core/api';
import { useAuth } from '@/core/auth';
import { useIntl } from '@/core/i18n/use-intl';
import { UserManagement } from '@/features/shared/users';
import { PageHeading } from '@/shared/components/layout';
import {
  Button,
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
  Input,
  Separator,
} from '@/shared/components/ui';

const customerFormSchema = z.object({
  name: z.string().min(1),
  companyType: z.string().optional(),
  industry: z.string().optional(),
  website: z.string().url().optional().or(z.literal('')),
});

type CustomerFormValues = z.infer<typeof customerFormSchema>;

export function ManageCustomer() {
  const { t } = useIntl();
  const [isLoading, setIsLoading] = useState(false);
  const [usage, setUsage] = useState({ userCount: 0, inviteCount: 0 });
  const { data: customer } = useCurrentUserCustomer();
  const updateCustomer = useUpdateCustomer();
  const { user: currentUser } = useAuth();

  const form = useForm<CustomerFormValues, any, CustomerFormValues>({
    resolver: zodResolver(customerFormSchema),
    defaultValues: {
      name: '',
      companyType: '',
      industry: '',
      website: '',
    },
  });

  useEffect(() => {
    if (customer) {
      form.reset({
        name: customer.name,
        companyType: customer.companyType || '',
        industry: customer.industry || '',
        website: customer.website || '',
      });
    }
  }, [customer, form]);

  const handleSubmit = async (data: CustomerFormValues) => {
    if (!customer || currentUser?.role !== 'customer-admin') return;
    setIsLoading(true);
    try {
      await updateCustomer.mutateAsync({ id: customer.id, data });
      toast.success(t('customer.customerUpdatedSuccessfully'));
    } catch (_error) {
      form.setError('root', {
        type: 'manual',
        message: t('errors.unknownError'),
      });
    } finally {
      setIsLoading(false);
    }
  };

  const canEditCustomer = currentUser?.role === 'customer-admin';
  const userQuota = customer?.userQuota ?? 0;
  const totalUsage = usage.userCount + usage.inviteCount;
  const handleUsageChange = useCallback((nextUsage: typeof usage) => {
    setUsage(nextUsage);
  }, []);

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <PageHeading>{t('customer.customer')}</PageHeading>
        <Button onClick={form.handleSubmit(handleSubmit)} disabled={isLoading || !canEditCustomer}>
          {isLoading ? t('common.saving') : t('common.save')}
        </Button>
      </div>

      <Form {...form}>
        <form onSubmit={form.handleSubmit(handleSubmit)} className="grid gap-6">
          <div className="grid gap-6 lg:grid-cols-12">
            <Card className="lg:col-span-8 xl:col-span-9">
              <CardHeader>
                <CardTitle>{t('customer.customer')}</CardTitle>
                <CardDescription>{t('customer.editCustomerDescription')}</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <FormField
                    control={form.control}
                    name="name"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel required>{t('common.name')}</FormLabel>
                        <FormControl>
                          <Input {...field} disabled={!canEditCustomer} />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  <FormField
                    control={form.control}
                    name="companyType"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>{t('customer.companyType')}</FormLabel>
                        <FormControl>
                          <Input {...field} disabled={!canEditCustomer} />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  <FormField
                    control={form.control}
                    name="industry"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>{t('customer.industry')}</FormLabel>
                        <FormControl>
                          <Input {...field} disabled={!canEditCustomer} />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  <FormField
                    control={form.control}
                    name="website"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>{t('customer.website')}</FormLabel>
                        <FormControl>
                          <Input {...field} disabled={!canEditCustomer} type="url" />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                </div>
              </CardContent>
            </Card>

            <Card className="h-fit lg:col-span-4 xl:col-span-3">
              <CardHeader>
                <CardTitle>{t('customer.userQuotaLabel')}</CardTitle>
                <CardDescription>{t('customer.userQuotaReadOnlyDescription')}</CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="space-y-2">
                  <FormLabel>{t('customer.userQuotaLabel')}</FormLabel>
                  <Input value={userQuota || ''} readOnly disabled />
                </div>
                <div className="space-y-1">
                  <p className="text-sm font-medium">
                    {t('customers.userQuotaUsage', {
                      used: totalUsage,
                      quota: userQuota,
                    })}
                  </p>
                  <p className="text-sm text-muted-foreground">
                    {t('customers.userQuotaUsageDetails', {
                      userCount: usage.userCount,
                      inviteCount: usage.inviteCount,
                    })}
                  </p>
                </div>
              </CardContent>
            </Card>
          </div>
        </form>
      </Form>

      <Separator />

      <UserManagement
        allowedRoles={['customer-admin']}
        title={t('users.users')}
        onUsageChange={handleUsageChange}
      />
    </div>
  );
}
